<template>
  <div class="field-configuration" v-if="show">
    <h3 class="field-configuration__header">
      <span>{{ field.type }}</span>
    </h3>
    <div class="field-configuration__required">
      <b>{{ $t('createEnvelope.documentSetup.fieldConfiguration.isRequired') }}</b>
      <span class="field-configuration__required__status">{{ configureField.required ? $t('functional.yes') : $t('functional.no') }}</span>
      <VCheckbox
        type="checkbox"
        subtype="toggle"
        size="small"
        :data-ec="ec"
        data-et="required_checkbox"
        :checked="configureField.required"
        v-model="configureField.required"
        @input="emitUpdated" />
    </div>
    <VGrid>

      <div slot="gridItem" type="3">
        <b>{{ $t('createEnvelope.documentSetup.fieldConfiguration.title') }}</b>
        <ElInput v-model="configureField.title" ref="titleInput" data-hj-suppress />
      </div>

      <div slot="gridItem" type="3" v-if="field.type === 'text'">
        <b>{{ $t('createEnvelope.documentSetup.fieldConfiguration.font') }}</b>
        <ElSelect v-model="configureField.font">
          <ElOption
            v-for="font in fonts"
            :key="font"
            :label="font"
            data-ec="field_configuration"
            :data-et="`font_option_${font}`"
            :value="font">
          </ElOption>
        </ElSelect>
      </div>

      <div slot="gridItem" type="4">
        <b>{{ $t('createEnvelope.documentSetup.fieldConfiguration.party') }}</b>
        <ElSelect
            v-model="configureField.party_id"
            v-if="configureField.party_id"
            data-hj-suppress
            data-ec="field_configuration"
            data-et="party_select_dropdown">
          <ElOption
            v-for="party in signingParties"
            :key="party.id"
            :label="party.title"
            :value="party.id">
          </ElOption>
        </ElSelect>
      </div>

      <div slot="gridItem" type="3" v-if="field.type === 'checkbox'">
        <b>{{ $t('createEnvelope.documentSetup.fieldConfiguration.groups') }}</b>
        <div class="field-configuration__field-controls-container">
          <span class="field-configuration__display-positioning">{{ $t('createEnvelope.documentSetup.fieldConfiguration.currentlyAssigned') }} {{ assignedCheckboxGroupLabel }}</span>
          <div class="field-configuration__field-controls">
            <VButton
              type="outline-1"
              data-ec="field_configuration"
              data-et="open_add_edit_checkbox_group_modal_button"
              :action="() => { openModal('add-edit-checkbox-groups-modal') }">
              <PlusCircleIcon v-if="configureField.group === ''" />
              <PencilIcon v-else />
              <span>{{ checkboxGroupAssignmentLabel }}</span>
            </VButton>
            <VButton
              type="outline-1"
              data-ec="field_configuration"
              data-et="unassign_checkbox_group_button"
              :action="unassignCheckboxGroup"
              :disabled="!configureField.group">
              <MinusCircleIcon />
              <span>Unassign</span>
            </VButton>
          </div>
        </div>
        <VDynamicLoader
          viewURL="modals/AddEditCheckboxGroupsModal"
          @updated="updatedCheckboxGroupChoice"
          :data="{ field: configureField }"
        />
      </div>

      <div slot="gridItem" type="4" v-if="field.type === 'dropdown'">
        <b>{{ $t('createEnvelope.documentSetup.fieldConfiguration.dropdownChoices') }}</b>

        <VButton
          type="outline-1"
          data-ec="field_configuration"
          data-et="open_add_edit_dropdown_choices_modal_button"
          :action="() => { openModal('dropdown-choices-modal') }">
          <PlusCircleIcon v-if="configureField.choices.length === 0" />
          <PencilIcon v-else />
          <span>{{ configureField.choices.length === 0
            ? $t('createEnvelope.documentSetup.fieldConfiguration.addYourChoices')
            : $t('createEnvelope.documentSetup.fieldConfiguration.editYourChoices') }}</span>
        </VButton>

        <VDynamicLoader
          viewURL="modals/DropdownChoices"
          v-model="configureField.choices"
          :data="{ field: configureField }"
        />
      </div>
      <div slot="gridItem" type="2" v-if="field.type === 'text' && isEnvelope && configureField.merge_field">
        <b>{{ $t('createEnvelope.documentSetup.fieldConfiguration.value') }} ({{ assignedMergeFieldLabel }})</b>
        {{ configureField.content }}
      </div>
      <div slot="gridItem" type="2" v-else-if="field.type === 'text' && prefillTypes !== undefined && !isEnvelope">
        <b>{{ $t('createEnvelope.documentSetup.fieldConfiguration.prefillType') }}</b>
        <div class="field-configuration__field-controls-container">
          <span class="field-configuration__display-positioning"><span v-if="!isEnvelope">{{ $t('createEnvelope.documentSetup.fieldConfiguration.currentlyAssigned') }} </span>{{ assignedMergeFieldLabel }}</span>
          <div class="field-configuration__field-controls" v-if="!isEnvelope">
            <VButton
              type="outline-1"
              data-ec="field_configuration"
              data-et="open_add_edit_prefill_types_modal_button"
              :disabled="!$store.getters['company/mergeFields']"
              :action="() => { openModal('add-edit-prefill-types-modal') }">
              <PlusCircleIcon v-if="configureField.merge_field === ''" />
              <PencilIcon v-else />
              <span>{{ prefillTypeAssignmentLabel }}</span>
            </VButton>
            <VButton
              type="outline-1"
              data-ec="field_configuration"
              data-et="unassign_merge_field_button"
              :action="unassignMergeField"
              :disabled="!configureField.merge_field">
              <MinusCircleIcon />
              <span>{{ $t('functional.unassign') }}</span>
            </VButton>
          </div>
        </div>
        <VDynamicLoader
          viewURL="modals/AddEditPrefillTypesModal"
          @updated="updatedMergeFieldChoice"
          :data="{ field: configureField, prefillTypes }"
        />
      </div>
    </VGrid>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VButton from '@/components/deprecated/atoms/Button.vue';
import VCheckbox from '@/components/deprecated/atoms/Checkbox.vue';
import VGrid from '@/components/deprecated/organisms/Grid.vue';
import VDynamicLoader from '@/components/DynamicLoader.vue';
import { MinusCircleIcon, PencilIcon, PlusCircleIcon } from '@/components/icons/fontIcons';

export default {
  name: 'VFieldConfiguration',
  props: {
    show: Boolean,
    ec: {
      type: String,
      required: false,
    },
    field: {
      type: [Object],
      required: true,
    },
    parties: {
      type: [Array],
      required: true,
    },
    isEnvelope: {
      type: [Boolean],
    },
  },
  components: {
    VButton,
    VCheckbox,
    VGrid,
    VDynamicLoader,
    MinusCircleIcon,
    PlusCircleIcon,
    PencilIcon,
  },
  data() {
    return {
      initialState: null,
      fonts: [
        'Arial',
        'Calibri',
        'Garamond',
        'Georgia',
        'Courier',
      ],
      configureField: {
        merge_field: '',
      },
    };
  },
  created() {
    this.setField();
    if (this.configureField.type === 'dropdown') {
      if (!this.configureField.choices) { // Set a default
        this.configureField.choices = [];
      }
    }
  },
  beforeDestroy() {
    this.checkIsValid();
  },
  updated() {
    this.currentMergeField = this.field.merge_field;
  },
  computed: {
    ...mapGetters({
      groups: 'company/groups',
      prefillTypes: 'company/mergeFields',
    }),
    signingParties() {
      return this.parties.filter(party => party.role === 'signer');
    },
    prefillTypeAssignmentLabel() {
      return this.configureField.merge_field ? this.$t('functional.change') : this.$t('functional.assign');
    },
    checkboxGroupAssignmentLabel() {
      return this.configureField.group ? this.$t('functional.change') : this.$t('functional.assign');
    },
    assignedMergeFieldLabel() {
      return this.configureField.merge_field ? this.configureField.merge_field : this.$t('functional.none');
    },
    assignedCheckboxGroupLabel() {
      return this.configureField.group ? this.configureField.group : this.$t('functional.none');
    },
  },
  methods: {
    setField() {
      this.initialState = JSON.stringify(this.field);
      this.configureField = JSON.parse(JSON.stringify(this.field));
    },
    checkIsValid() {
      if (this.configureField) {
        if (this.configureField.type === 'dropdown') {
          if (this.configureField.choices && !this.configureField.choices.length) {
            this.$notify({
              title: this.$t('createEnvelope.documentSetup.fieldConfiguration.validation.title'),
              message: this.$t('createEnvelope.documentSetup.fieldConfiguration.validation.dropdownMissingOptions'),
              position: 'bottom left',
              type: 'error',
            });
          }
        }
        if (JSON.stringify(this.configureField) !== this.initialState) {
          this.emitUpdated(); // Broadcast out that we've updated the field
        }
      }
    },
    emitUpdated() {
      this.$emit('updated', {
        field: this.configureField,
        changed: true,
      });
    },
    openModal(modal) {
      this.$bus.$emit('closeModal');
      this.$bus.$emit('toggleModal', { name: modal, active: true });
    },
    updatedFieldChoices(choices) {
      this.configureField.choices = choices.list;
      this.emitUpdated();
    },
    unassignMergeField() {
      this.configureField.merge_field = '';
      this.field.merge_field = '';
      this.emitUpdated();
    },
    unassignCheckboxGroup() {
      this.configureField.group = '';
      this.field.group = '';
      this.emitUpdated();
    },
    updatedMergeFieldChoice(choice) {
      this.configureField.merge_field = choice;
      this.emitUpdated();
    },
    updatedCheckboxGroupChoice(choice) {
      this.configureField.group = choice;
      this.emitUpdated();
    },
    onSelectParty(selected) {
      const party = this.parties.find(item => selected.id === item.id);
      this.configureField.party = party;
      this.emitUpdated();
    },
  },
  watch: {
    field: {
      handler() {
        this.setField();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.field-configuration {
  padding: var(--spacing-base);
  background: #fff;
  border-top: 1px solid var(--color-border);
  z-index: 1;
  text-transform: capitalize;
}

.field-configuration__field-controls {
  margin-top: var(--spacing-small);
}

.field-configuration__field-controls a {
  margin: 0 var(--spacing-small) var(--spacing-small) 0;
}

.field-configuration__display-positioning {
  float: left;
}

.field-configuration__field-controls-container {
  display: flex;
  flex-direction: column;
}

.field-configuration__header {
  display: inline-block;
}

.field-configuration__required {
  float: right;
  display: inline;
}

.field-configuration__required b {
  vertical-align: middle;
  display: inline !important;
  margin-right: var(--spacing-xs);
}

.field-configuration__required__status {
  display: inline;
  vertical-align: middle;
  margin-right: var(--spacing-xs);
}

.field-configuration__required .a-checkbox {
  vertical-align: middle;
}

.field-configuration b {
  display: block;
  margin-bottom: var(--spacing-small);
  font-family: 'Roboto', sans-serif !important;
}

.field-configuration ::v-deep .a-button {
  font-size: 14px;
  padding: 9px 10px;
}
</style>
